// ** react imports
import React from 'react';

// ** router imports
import { Link } from 'react-router-dom';

// ** components imports
import Badge from '../../common/Badge';

// ** enums imports
import { TAX_FILE_TYPE, BADGE_THEMES } from '../../../enums/common';

// ** utils imports
import { formatPhoneNumber, formatUtcToLocal } from '../../../utils/Utils';

const CustomerInformationSection = props => {
  // props
  const { taxFile } = props;

  // handlers
  const getStatusBadge = status => {
    const statusThemeMap = {
      CREATED: BADGE_THEMES.CREATED,
      IN_PROGRESS: BADGE_THEMES.IN_PROGRESS,
      COMPLETED: BADGE_THEMES.COMPLETED,
      SUBMITTED: BADGE_THEMES.SUBMITTED,
      PROCESSED: BADGE_THEMES.PROCESSED,
      ARCHIVED: BADGE_THEMES.ARCHIVED,
    };

    const theme = statusThemeMap[status] || BADGE_THEMES.CREATED;
    const statusText = (status || 'CREATED').replace('_', ' ');

    return <Badge theme={theme} text={statusText} />;
  };

  return (
    <section className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div className="h-full md:h-[95px] px-4 py-4 sm:px-6 sm:py-5 bg-gradient-to-r from-violet-50 to-purple-50 dark:from-violet-900/20 dark:to-purple-900/20 border-b border-gray-200 dark:border-gray-700">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center space-x-2.5">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg flex-shrink-0">
              <svg
                className="w-5 h-5 sm:w-6 sm:h-6 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                />
              </svg>
            </div>
            <div>
              <div className="flex justify-start items-center gap-3">
                <h2 className="text-lg sm:text-xl font-bold flex items-center flex-wrap gap-2 text-sm text-gray-800 dark:text-gray-100 tracking-wide uppercase">
                  {taxFile.clientInfo?.fullName || 'N/A'}
                </h2>

                {taxFile.clientInfo?.isWalkIn && (
                  <Badge theme={BADGE_THEMES.walk_in} text="Walk-in Customer" />
                )}
              </div>
              <div className="flex justify-start items-center gap-3">
                <Link
                  to={`/customers/details/${taxFile.userId?._id}?page=profile`}
                >
                  <p className="text-xs text-gray-800 dark:text-gray-100 font-medium font-mono mt-2">
                    ID: {taxFile.userId?.userId || 'N/A'},
                  </p>
                </Link>

                {taxFile.clientInfo?.email && (
                  <p className="text-xs text-gray-800 dark:text-gray-100 font-medium font-mono mt-2">
                    Email: {taxFile.clientInfo?.email || 'N/A'}
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="flex justify-start sm:justify-end">
            {getStatusBadge(taxFile.fileStatus)}
          </div>
        </div>
      </div>

      <div className="h-full md:h-[180px] p-4 sm:p-6 space-y-5 sm:space-y-6">
        <div className="grid grid-cols-2 gap-5">
          <div className="">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-green-100 dark:bg-green-900/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <svg
                  className="w-4 h-4 text-green-600 dark:text-green-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                  />
                </svg>
              </div>
              <div>
                <p className="text-base text-gray-800 dark:text-gray-100 font-medium">
                  {formatPhoneNumber(taxFile.clientInfo?.phone)}
                </p>
              </div>
            </div>
          </div>

          <div className="">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <svg
                  className="w-4 h-4 text-blue-600 dark:text-blue-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <div>
                <p className="text-base text-gray-800 dark:text-gray-100 font-medium">
                  {taxFile.taxYear || 'N/A'}
                </p>
              </div>
            </div>
          </div>

          <div className="">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center flex-shrink-0">
                <svg
                  className="w-4 h-4 text-gray-600 dark:text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </div>
              <div>
                <Badge
                  theme={
                    taxFile.submissionType === TAX_FILE_TYPE.VIRTUAL_INTAKE
                      ? BADGE_THEMES.PRIMARY
                      : BADGE_THEMES.WARNING
                  }
                  text={
                    taxFile.submissionType === TAX_FILE_TYPE.VIRTUAL_INTAKE
                      ? 'Virtual Intake'
                      : 'In-Office'
                  }
                />
              </div>
            </div>
          </div>

          <div className="">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-amber-100 dark:bg-amber-900/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <svg
                  className="w-4 h-4 text-amber-600 dark:text-amber-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div>
                <p className="text-base text-gray-800 dark:text-gray-100 font-medium">
                  {formatUtcToLocal(taxFile.createdAt)}
                </p>
              </div>
            </div>
          </div>

          {taxFile.assignedTo && (
            <div className="">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-indigo-100 dark:bg-indigo-900/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg
                    className="w-4 h-4 text-indigo-600 dark:text-indigo-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                </div>
                <div>
                  <p className="text-base text-gray-800 dark:text-gray-100 font-medium">
                    {typeof taxFile.assignedTo === 'object'
                      ? taxFile.assignedTo.name ||
                        taxFile.assignedTo.email ||
                        taxFile.assignedTo._id ||
                        'N/A'
                      : taxFile.assignedTo || 'N/A'}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default CustomerInformationSection;
